import { useRef, useState } from 'react';
import { BookOpen, ExternalLink, Upload } from 'lucide-react';
import { Button, DataTable, ErrorState, FormField, PageHeader, Panel, StatusBadge, TextField, useToast, type Column } from '@/components';
import { ApiError, errorMessage } from '@/lib/api';
import { formatDateTime, formatNumber } from '@/lib/format';
import { useManuals, useUploadManual, type ManualDto } from './api';
import './admin.css';

/** S-94 マニュアル管理（基金のみ）。アップロードのたびに版が上がり、最新版がポータルから開かれる */
export function ManualsPage() {
  const toast = useToast();
  const list = useManuals();
  const upload = useUploadManual();
  const fileRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [fileError, setFileError] = useState<string>();
  const [titleError, setTitleError] = useState<string>();

  const onUpload = async () => {
    setFileError(undefined);
    setTitleError(undefined);
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setFileError('ファイルを選択してください');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title);
    if (note) fd.append('note', note);
    try {
      const r = await upload.mutateAsync(fd);
      toast.success('マニュアルをアップロードしました', `第${r.version}版：${r.title}`);
      setTitle('');
      setNote('');
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      if (e instanceof ApiError && (e.fieldErrors.file || e.fieldErrors.title)) {
        setFileError(e.fieldErrors.file);
        setTitleError(e.fieldErrors.title);
      } else toast.error('アップロードできませんでした', errorMessage(e));
    }
  };

  const columns: Column<ManualDto>[] = [
    { key: 'version', header: '版', numeric: true, width: 70, render: (m) => `第${m.version}版` },
    { key: 'title', header: 'タイトル', render: (m) => <strong>{m.title}</strong> },
    { key: 'fileName', header: 'ファイル名', width: 240 },
    { key: 'sizeBytes', header: 'サイズ', numeric: true, width: 110, render: (m) => `${formatNumber(Math.ceil(m.sizeBytes / 1024))} KB` },
    { key: 'uploadedAt', header: 'アップロード日時', width: 170, render: (m) => <span className="bs-num">{formatDateTime(m.uploadedAt)}</span> },
    { key: 'isLatest', header: '', width: 90, align: 'center', render: (m) => (m.isLatest ? <StatusBadge tone="accent" dot>最新版</StatusBadge> : null) },
  ];

  return (
    <>
      <PageHeader
        eyebrow="システム管理"
        title="マニュアル管理"
        description="操作マニュアルをアップロードします。ポータルの「操作マニュアル」は常に最新版を開きます。"
        actions={
          <a className="bs-btn bs-btn--secondary bs-manual-open" href="/api/manuals/latest" target="_blank" rel="noopener noreferrer">
            <ExternalLink size={16} aria-hidden /> 最新版を開く
          </a>
        }
      />
      <Panel title="新しい版をアップロード" description="PDF・Word・Excel・PowerPoint・ZIP（30MB まで）">
        <div className="bs-upload">
          <FormField label="タイトル" error={titleError} hint="省略するとファイル名を使います">
            <TextField value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
          </FormField>
          <FormField label="ファイル" required error={fileError}>
            <div className="bs-file">
              <input ref={fileRef} type="file" accept=".pdf,.docx,.xlsx,.pptx,.zip" aria-label="マニュアルのファイル" />
            </div>
          </FormField>
          <FormField label="備考">
            <TextField value={note} onChange={(e) => setNote(e.target.value)} />
          </FormField>
          <Button variant="primary" icon={<Upload size={16} />} onClick={onUpload} loading={upload.isPending}>
            アップロード
          </Button>
        </div>
      </Panel>
      <Panel title="版の履歴" flush>
        {list.error ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : (
          <DataTable
            caption="マニュアルの版の履歴"
            columns={columns}
            rows={list.data ?? []}
            rowKey={(m) => m.id}
            loading={list.isPending}
            dense
            empty={<span><BookOpen size={16} style={{ verticalAlign: -3 }} /> マニュアルはまだアップロードされていません</span>}
          />
        )}
      </Panel>
    </>
  );
}
